/**
 * ZEN ERP — Dashboard Hero Banner (Hoş Geldin + Kısayollar + Duyurular)
 * DashboardPage.jsx'ten çıkarıldı.
 * İçerir: BotHealthPill, JspmHealthPill (internal), DashboardHeroBanner (export)
 */
import React, { useState, useRef, useEffect } from "react";
import { Bell, Upload, Check, X, FileSignature, FileText, Trash2, Bot, Printer } from "lucide-react";
import { toast } from "sonner";
import { useJspm } from "../../lib/jspm";
import api from "../../api";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import CountUp from "../../components/anim/CountUp";

gsap.registerPlugin(useGSAP);

// ── Bot Sağlık Göstergesi ──
function BotHealthPill({ setTab }) {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/bots/status");
        if (!cancelled) setStatus(data);
      } catch (e) { /* sessiz */ }
    };
    load();
    const t = setInterval(load, 60 * 1000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);
  if (!status) return null;
  const tgLive = status.telegram?.live;
  const waLive = status.whatsapp?.live;
  const both = tgLive && waLive;
  const any = tgLive || waLive;
  const cls = both
    ? "bg-emerald-500/25 border-emerald-400/50 text-emerald-50"
    : (any ? "bg-amber-500/30 border-amber-400/60 text-amber-50 animate-pulse" : "bg-rose-500/30 border-rose-400/60 text-rose-50 animate-pulse");
  const label = both ? "Bot" : (any ? "Bot!" : "Bot✕");
  const openBotsHub = () => {
    window.__zenSettingsInit = { tab: "bots_hub" };
    setTab && setTab("settings");
  };
  return (
    <button
      onClick={openBotsHub}
      data-testid="dashboard-bot-health-pill"
      title={`Telegram: ${tgLive ? "canlı" : "hata"} · WhatsApp: ${waLive ? "canlı" : "hata"} — Bot & Haberleşme Merkezi'ne git`}
      className={`rounded-md px-1.5 py-0.5 border shadow-[inset_0_1px_1px_rgba(255,255,255,0.15)] backdrop-blur-md flex items-center gap-1 transition-all hover:-translate-y-0.5 ${cls}`}
    >
      <Bot className="w-2.5 h-2.5" />
      <span className="text-[9px] font-extrabold tracking-tight">{label}</span>
    </button>
  );
}

// ── JSPM Yazıcı Sağlık Göstergesi ──
function JspmHealthPill({ setTab }) {
  const { connected, connecting, printers, statusLabel } = useJspm();
  const [rolesOk, setRolesOk] = useState(true);
  useEffect(() => {
    if (!connected) { setRolesOk(false); return; }
    let cancelled = false;
    api.get("/settings", { params: { category: "jspm_printers" } })
      .then(({ data }) => {
        if (cancelled) return;
        const anyAssigned = (data || []).some((r) => (r.value || "").trim().length > 0);
        setRolesOk(anyAssigned && printers.length > 0);
      })
      .catch(() => setRolesOk(false));
    return () => { cancelled = true; };
  }, [connected, printers]);
  const cls = connecting
    ? "bg-amber-500/25 border-amber-400/50 text-amber-50 animate-pulse"
    : (!connected
        ? "bg-rose-500/30 border-rose-400/60 text-rose-50 animate-pulse"
        : (!rolesOk ? "bg-amber-500/30 border-amber-400/60 text-amber-50 animate-pulse"
                    : "bg-emerald-500/25 border-emerald-400/50 text-emerald-50"));
  const label = connecting ? "JSPM…" : (!connected ? "JSPM✕" : (!rolesOk ? "JSPM!" : "JSPM"));
  const openJspm = () => {
    window.__zenSettingsInit = { tab: "printers", innerTab: "jspm" };
    setTab && setTab("settings");
  };
  return (
    <div className="flex items-center gap-1">
      <button
        onClick={openJspm}
        data-testid="dashboard-jspm-health-pill"
        title={`JSPM Yazıcı Bağlantısı — ${statusLabel} · Yazıcı: ${printers.length}${connecting ? " · Bağlanıyor" : (!connected ? " · Bağlı Değil" : (!rolesOk ? " · Rol Atanmamış" : ""))}`}
        className={`rounded-md px-1.5 py-0.5 border shadow-[inset_0_1px_1px_rgba(255,255,255,0.15)] backdrop-blur-md flex items-center gap-1 transition-all hover:-translate-y-0.5 ${cls}`}
      >
        <Printer className="w-2.5 h-2.5" />
        <span className="text-[9px] font-extrabold tracking-tight">{label}</span>
      </button>
    </div>
  );
}

// ── Dashboard Hero Banner ──
// Props: user, rates, summary, shortcuts, tr, lang, isAdmin, setTab
export function DashboardHeroBanner({ user, rates, summary, shortcuts, tr, lang, isAdmin, setTab, onTeklifHazirla }) {
  const [anns, setAnns] = useState([]);
  const [annOpen, setAnnOpen] = useState(false);
  const [annText, setAnnText] = useState("");
  const [annImg, setAnnImg] = useState("");
  const [annSaving, setAnnSaving] = useState(false);
  const annFileRef = useRef(null);

  // GSAP giriş animasyonları (hızlı ve ince — günlük kullanım ekranı)
  // GÜVENLİK AĞI: animasyon yarıda kalsa bile içerik 2 sn içinde mutlaka görünür
  useGSAP(() => {
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.fromTo("[data-anim='hero-kpi']", { y: 14, opacity: 0 }, { y: 0, opacity: 1, duration: 0.5, ease: "power2.out", stagger: 0.1, delay: 0.15, clearProps: "transform,opacity" });
      gsap.fromTo("[data-anim='hero-shortcut']", { y: 18, opacity: 0 }, { y: 0, opacity: 1, duration: 0.45, ease: "power2.out", stagger: 0.05, delay: 0.25, clearProps: "transform,opacity" });
    });
    const safety = setTimeout(() => {
      document.querySelectorAll("[data-anim='hero-kpi'], [data-anim='hero-shortcut']").forEach((el) => {
        el.style.opacity = ""; el.style.transform = "";
      });
    }, 2000);
    return () => { clearTimeout(safety); mm.revert(); };
  });

  const loadAnns = async () => {
    try { const { data } = await api.get("/announcements"); setAnns(data || []); } catch (e) {}
  };
  useEffect(() => { loadAnns(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const pickAnnImage = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const maxW = 1000;
        const scale = Math.min(1, maxW / img.width);
        const cv = document.createElement("canvas");
        cv.width = Math.round(img.width * scale); cv.height = Math.round(img.height * scale);
        cv.getContext("2d").drawImage(img, 0, 0, cv.width, cv.height);
        setAnnImg(cv.toDataURL("image/jpeg", 0.82));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };
  const saveAnn = async () => {
    if (!annText.trim() && !annImg) { toast.error("Duyuru metni veya görsel ekleyin"); return; }
    setAnnSaving(true);
    try {
      await api.post("/announcements", { text: annText.trim(), imageBase64: annImg });
      setAnnText(""); setAnnImg(""); setAnnOpen(false);
      await loadAnns();
      toast.success("Duyuru yayınlandı");
    } catch (e) { toast.error("Duyuru kaydedilemedi"); }
    setAnnSaving(false);
  };
  const deleteAnn = async (id) => {
    if (!window.confirm("Duyuru silinsin mi?")) return;
    await api.delete(`/announcements/${id}`);
    loadAnns();
  };

  return (
    <div
      className="zen-hero-liquid relative overflow-hidden rounded-2xl border border-indigo-500/30
                 bg-gradient-to-br from-[#433BC2] via-[#3930AC] to-[#2F298B]
                 text-white shadow-xl shadow-indigo-900/30"
    >
      {/* Ambient blobs */}
      <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-white/10 blur-3xl" />
      <div className="absolute -bottom-20 -left-16 w-56 h-56 rounded-full bg-fuchsia-500/15 blur-3xl" />

      {/* Teklif Hazırla + İstatistikler — tek satır, buton diğer butonlarla aynı genişlikte */}
      <div className="zen-hero-rise relative px-3 pt-3 pb-1">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">

          {/* ── Teklif Hazırla — ızgara butonu (diğer butonlarla aynı boyut) ── */}
          <button
            data-testid="new-order-hero-btn"
            onClick={onTeklifHazirla || (() => setTab && setTab("quotes"))}
            className="group flex flex-col items-center justify-center gap-1.5 py-3 px-2
                       rounded-xl bg-white/10 hover:bg-white/20 backdrop-blur-md
                       border border-white/15 hover:border-white/30
                       transition-all duration-150 hover:-translate-y-0.5"
          >
            <div className="relative w-9 h-9 rounded-[11px] bg-gradient-to-br from-[#8FB8C6] via-[#4F8394] to-[#2C5561]
                            flex items-center justify-center shrink-0 shadow-md shadow-black/25 ring-1 ring-black/10
                            transition-transform duration-150 group-hover:scale-110">
              <span className="pointer-events-none absolute inset-0 rounded-[11px] bg-gradient-to-b from-white/28 via-white/8 to-transparent" />
              <FileSignature className="relative w-[18px] h-[18px] text-white drop-shadow-[0_1px_1px_rgba(0,0,0,0.3)]" strokeWidth={2} />
            </div>
            <div className="text-[11px] font-semibold text-white text-center leading-tight">{tr("Teklif Hazırla")}</div>
          </button>

          {/* ── Verilen Teklifler — Teklif Hazırla'nın hemen yanı ── */}
          <button
            data-testid="quotes-hero-btn"
            onClick={() => setTab && setTab("quotes")}
            className="group flex flex-col items-center justify-center gap-1.5 py-3 px-2
                       rounded-xl bg-white/10 hover:bg-white/20 backdrop-blur-md
                       border border-white/15 hover:border-white/30
                       transition-all duration-150 hover:-translate-y-0.5"
          >
            <div className="relative w-9 h-9 rounded-[11px] bg-gradient-to-br from-[#8FA9C6] via-[#4F6E94] to-[#2C3F61]
                            flex items-center justify-center shrink-0 shadow-md shadow-black/25 ring-1 ring-black/10
                            transition-transform duration-150 group-hover:scale-110">
              <span className="pointer-events-none absolute inset-0 rounded-[11px] bg-gradient-to-b from-white/28 via-white/8 to-transparent" />
              <FileText className="relative w-[18px] h-[18px] text-white drop-shadow-[0_1px_1px_rgba(0,0,0,0.3)]" strokeWidth={2} />
            </div>
            <div className="text-[11px] font-semibold text-white text-center leading-tight">{tr("Verilen Teklifler")}</div>
          </button>

          {/* ── Sağ: Pill'ler + İstatistikler ── */}
          <div className="col-span-2 sm:col-span-3 lg:col-span-4 flex items-center justify-end gap-1.5 text-[11px]">
            {isAdmin && (
              <div className="flex items-center gap-1">
                <BotHealthPill setTab={setTab} />
                <JspmHealthPill setTab={setTab} />
              </div>
            )}
            <div className="hidden md:flex items-center gap-1.5">
              <div data-anim="hero-kpi" className="rounded-lg bg-white/10 backdrop-blur-md px-2.5 py-1.5 border border-white/15 min-w-[92px] shadow-[inset_0_1px_1px_rgba(255,255,255,0.15)]">
                <div className="text-white/70 text-[10px] uppercase tracking-wider">{tr("Toplam Sipariş")}</div>
                <div className="zen-mono text-xl font-extrabold"><CountUp to={Number(summary.totalOrders) || 0} duration={1.2} /></div>
              </div>
              <div data-anim="hero-kpi" className="rounded-lg bg-white/10 backdrop-blur-md px-2.5 py-1.5 border border-white/15 min-w-[92px] shadow-[inset_0_1px_1px_rgba(255,255,255,0.15)]">
                <div className="text-white/70 text-[10px] uppercase tracking-wider">{tr("Cari Sayısı")}</div>
                <div className="zen-mono text-xl font-extrabold"><CountUp to={Number(summary.customerCount) || 0} duration={1.2} delay={0.12} /></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick-action cards */}
      <div className="zen-hero-rise relative px-3 pt-1 pb-3" style={{ animationDelay: "0.12s" }}>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
          {shortcuts.map((s) => {
            if (s.spacer) return <div key={s.key} data-testid="shortcut-spacer" />;
            const Icon = s.icon;
            return (
              <button
                key={s.key}
                onClick={s.onClick}
                data-testid={`shortcut-${s.key}`}
                data-anim="hero-shortcut"
                className="group flex flex-col items-center justify-center gap-1.5 py-3 px-2
                           rounded-xl bg-white/10 hover:bg-white/20 backdrop-blur-md
                           border border-white/15 hover:border-white/30
                           transition-all duration-150 hover:-translate-y-0.5"
              >
                <div className={`relative w-9 h-9 rounded-[11px] bg-gradient-to-br ${s.color} flex items-center justify-center shrink-0 shadow-md shadow-black/25 ring-1 ring-black/10 transition-transform duration-150 group-hover:scale-110`}>
                  <span className="pointer-events-none absolute inset-0 rounded-[11px] bg-gradient-to-b from-white/28 via-white/8 to-transparent" />
                  <Icon className="relative w-4.5 h-4.5 text-white drop-shadow-[0_1px_1px_rgba(0,0,0,0.3)]" strokeWidth={2} />
                </div>
                <div className="text-[11px] font-semibold text-white text-center leading-tight">{s.label}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Duyuru bandı */}
      <div className="relative mx-4 mb-4 flex items-start gap-3 px-4 py-3 rounded-xl bg-white/10 border border-white/15">
        <button
          type="button"
          data-testid="announcement-bell-btn"
          onClick={() => { if (isAdmin) setAnnOpen((v) => !v); }}
          title={isAdmin ? tr("Duyuru ekle") : tr("Duyurular")}
          className={`w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center shrink-0 transition-all ${isAdmin ? "hover:bg-white/30 hover:scale-105 cursor-pointer" : "cursor-default"}`}
        >
          <Bell className="w-4 h-4 text-white" />
        </button>
        <div className="flex-1 min-w-0">
          {annOpen && isAdmin && (
            <div className="mb-3 space-y-2" data-testid="announcement-composer">
              <textarea
                data-testid="announcement-text-input"
                value={annText}
                onChange={(e) => setAnnText(e.target.value)}
                rows={2}
                placeholder={tr("Duyuru metnini yazın...")}
                className="w-full rounded-lg bg-white/15 border border-white/25 px-3 py-2 text-[12px] text-white placeholder-white/50 outline-none focus:border-white/50 resize-y"
              />
              {annImg && (
                <div className="relative inline-block">
                  <img src={annImg} alt="" className="max-h-40 rounded-lg border border-white/25" />
                  <button type="button" data-testid="announcement-image-remove" onClick={() => setAnnImg("")} className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-black/70 flex items-center justify-center"><X className="w-3.5 h-3.5 text-white" /></button>
                </div>
              )}
              <div className="flex items-center gap-2">
                <input ref={annFileRef} type="file" accept="image/*" className="hidden" data-testid="announcement-image-input" onChange={(e) => pickAnnImage(e.target.files?.[0])} />
                <button type="button" data-testid="announcement-image-btn" onClick={() => annFileRef.current?.click()} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/15 hover:bg-white/25 border border-white/25 text-[11px] font-semibold transition-colors">
                  <Upload className="w-3.5 h-3.5" /> {tr("Resim Ekle")}
                </button>
                <button type="button" data-testid="announcement-save-btn" disabled={annSaving} onClick={saveAnn} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white text-indigo-700 text-[11px] font-bold hover:shadow-lg disabled:opacity-60 transition-all">
                  <Check className="w-3.5 h-3.5" /> {annSaving ? tr("Kaydediliyor...") : tr("Yayınla")}
                </button>
                <button type="button" data-testid="announcement-cancel-btn" onClick={() => { setAnnOpen(false); setAnnText(""); setAnnImg(""); }} className="px-3 py-1.5 rounded-lg text-[11px] font-semibold text-white/70 hover:text-white transition-colors">
                  {tr("Vazgeç")}
                </button>
              </div>
            </div>
          )}
          {anns.length === 0 ? (
            <div className="text-[12px] text-white/85" data-testid="announcement-empty">
              {isAdmin ? tr("Henüz duyuru yok. Zil ikonuna tıklayarak duyuru ekleyebilirsiniz.") : tr("Şu an için herhangi bir duyuru bulunmamaktadır.")}
            </div>
          ) : (
            <div className="space-y-3" data-testid="announcement-list">
              {anns.map((a) => (
                <div key={a.id} className="group" data-testid={`announcement-${a.id}`}>
                  <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                      {a.text && <div className="text-[12px] text-white/90 whitespace-pre-wrap break-words">{a.text}</div>}
                      {a.imageBase64 && (
                        <img src={a.imageBase64} alt="Duyuru" className="mt-2 rounded-lg border border-white/20 w-auto max-w-full h-auto" data-testid={`announcement-img-${a.id}`} />
                      )}
                      <div className="text-[10px] text-white/50 mt-1">
                        {a.createdBy ? `${a.createdBy} · ` : ""}{new Date(a.createdAt).toLocaleString("tr-TR")}
                      </div>
                    </div>
                    {isAdmin && (
                      <button type="button" data-testid={`announcement-delete-${a.id}`} onClick={() => deleteAnn(a.id)} title={tr("Duyuruyu sil")} className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg hover:bg-white/20 transition-all shrink-0">
                        <Trash2 className="w-3.5 h-3.5 text-white" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
